import React, { useEffect, useState } from 'react';
import { ArrowRight, Star, Instagram, Phone, ShieldCheck, Truck, Gift } from 'lucide-react'; // Added Instagram/Phone icons
import axiosInstance, { BACKEND_ROOT_URL } from '../../utils/axiosInstance';
import ProductCard from '../components/ProductCard';
import { Link, useNavigate } from 'react-router-dom';

const FashionJewelryStore = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [blogs, setBlogs] = useState([]);
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        const { data } = await axiosInstance.get(`/blogs`);
        setBlogs(data);
      } catch (error) {
        console.error('Error fetching blogs', error);
      }
    };
    fetchBlogs();
  }, []);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axiosInstance.get('/categories');
        setCategories(response.data.sort((a, b) => a.sequence - b.sequence));
      } catch (error) {
        console.error("Failed to fetch categories", error);
      }
    };
    fetchCategories();
  }, []);

  // Palette:
  // Plum: #551756 (Primary Brand Color)
  // Gold: #CAB273 (Accents)
  // Cream: #EBE6DE (Background)

  return (
    <div className="min-h-screen bg-[#EBE6DE] font-sans text-[#551756]">
      
      {/* --- HERO SECTION --- */}
      <div className="relative h-[85vh] w-full overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src={BACKEND_ROOT_URL+'/uploads/hero/hero.png'}
            alt="Fashion Jewelry Model" 
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#551756]/60 to-transparent"></div>
        </div>
        
        {/* Content */}
        <div className="relative h-full flex flex-col justify-center items-start text-left px-4 md:px-20 max-w-7xl mx-auto">
          <p className="text-[#CAB273] uppercase tracking-[0.2em] mb-4 text-sm font-bold">Affordable Luxury</p>
          <h1 className="font-serif text-5xl md:text-7xl text-[#EBE6DE] mb-6 leading-tight drop-shadow-lg">
            Royal Looks <br/> <span className="italic text-[#CAB273]">Budget Price</span>
          </h1>
          <p className="text-[#EBE6DE] text-lg mb-8 max-w-md">
            Stunning imitation designs ranging from Kundan to American Diamond. Get the perfect festive look without breaking the bank.
          </p>
          <Link to={'/products'} className="bg-[#CAB273] text-[#551756] px-10 py-4 uppercase tracking-widest text-sm font-bold hover:bg-[#EBE6DE] hover:text-[#551756] transition-all duration-300 shadow-lg">
            Shop New Arrivals
          </Link>
        </div>
      </div>

      {/* --- ICONS / VALUE PROPS (Updated for Imitation) --- */}
      <div className="py-8 border-b border-[#551756]/10 bg-[#EBE6DE]">
        <div className="max-w-7xl mx-auto px-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            {['Premium Finish', 'Skin Friendly', 'Lightweight', 'Best Prices'].map((prop) => (
                <div key={prop} className="text-[#551756] text-xs uppercase tracking-widest font-semibold flex items-center justify-center gap-2">
                    <Star className="w-3 h-3 fill-[#CAB273] text-[#CAB273]" /> {prop}
                </div>
            ))}
        </div>
      </div>

      {/* --- CATEGORIES GRID --- */}
      <section className="max-w-7xl mx-auto px-4 py-20">
        <div className="text-center mb-16">
          <h2 className="font-serif text-3xl md:text-4xl mb-4">Complete Your Look</h2>
          <p className="text-[#551756]/70">From heavy bridal sets to daily wear earrings.</p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {categories.map((cat) => (
            <div key={cat.name} className="group relative h-[450px] cursor-pointer overflow-hidden rounded-sm">
              <img 
                src={BACKEND_ROOT_URL+'/'+cat.image} 
                alt={cat.name} 
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#551756]/80 to-transparent opacity-60 group-hover:opacity-80 transition-opacity"></div>
              <div className="absolute bottom-6 left-0 right-0 text-center">
                <h3 className="text-2xl font-serif text-[#EBE6DE] mb-2">{cat.name}</h3>
                <button onClick={()=>{navigate(`/products?category=${cat.id}`)}} className="text-[#CAB273] text-xs uppercase tracking-widest font-bold underline decoration-[#CAB273] underline-offset-4">
                  Shop Now
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* --- FEATURE SPLIT SECTION (Updated Text) --- */}
      <section className="flex flex-col md:flex-row bg-[#551756] text-[#EBE6DE]">
        <div className="md:w-1/2 flex flex-col justify-center p-12 md:p-20 text-left order-2 md:order-1">
          <div className="flex items-center gap-2 text-[#CAB273] mb-4">
            <span className="uppercase tracking-widest text-xs font-bold">The Art of Details</span>
          </div>
          <h2 className="font-serif text-4xl md:text-5xl mb-6 leading-tight">
            Beauty in Every <br/><span className="text-[#CAB273] italic">Sparkle</span>
          </h2>
          <p className="text-[#EBE6DE]/80 mb-8 leading-relaxed max-w-md">
            Why spend lakhs when you can dazzle for less? Our pieces are intricately crafted with high-quality stones and polish to mimic the brilliance of real jewelry. Perfect for weddings, parties, or adding a touch of glam to your daily outfit.
          </p>
          <Link to={'/products'} className="self-start border border-[#CAB273] text-[#CAB273] px-8 py-3 uppercase tracking-widest text-sm font-bold hover:bg-[#CAB273] hover:text-[#551756] transition-all duration-300">
            View Collection
          </Link>
        </div>
        <div className="md:w-1/2 relative h-[500px] md:h-auto order-1 md:order-2">
          <img 
                src={BACKEND_ROOT_URL+'/uploads/hero/promo.png'}
            alt="Jewelry styling" 
            className="w-full h-full object-cover"
          />
        </div>
      </section>

      {/* --- TRENDING / NEW ARRIVALS --- */}
      <section className="max-w-7xl mx-auto px-4 py-20">
        <div className="flex justify-between items-end mb-12">
          <h2 className="font-serif text-3xl text-[#551756]">Trending Now</h2>
          <Link to={'/products'} className="flex items-center text-sm uppercase tracking-widest font-bold hover:text-[#CAB273] transition-colors">
            View All <ArrowRight className="ml-2 w-4 h-4" />
          </Link>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {blogs && blogs.length > 0 && blogs.slice(0, 8).map((item) => (
                <ProductCard key={item.id} item={item} />
            ))}
        </div>
      </section>

      {/* --- COMMUNITY / SOCIAL SECTION (Replaced Newsletter) --- */}
     {/* --- SERVICE PROMISE / EXPERIENCE SECTION --- */}
      <section className="bg-[#CAB273]/10 py-20 px-4 border-t border-[#551756]/5">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-serif text-3xl md:text-4xl mb-4 text-[#551756]">The Royal Experience</h2>
            <p className="text-[#551756]/80 text-lg max-w-2xl mx-auto">
              We believe imitation jewelry should feel just as special as the real thing. 
              Here is what you get with every order.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
             {/* Feature 1: Packaging */}
          <div className="flex flex-col items-center text-center p-8 bg-white shadow-lg border-t-4 border-[#CAB273] hover:-translate-y-1 transition-transform duration-300">
    <div className="bg-[#551756]/5 p-4 rounded-full mb-6">
        <Gift className="w-8 h-8 text-[#551756]" />
    </div>
    <h3 className="font-serif text-xl text-[#551756] mb-3 font-bold">The Perfect Gift</h3>
    <p className="text-[#551756]/70 leading-relaxed">
        Jewelry is the language of love. Whether for a birthday, anniversary, or a treat for yourself, our designs make every moment memorable.
    </p>
</div>

             {/* Feature 2: Shipping */}
             <div className="flex flex-col items-center text-center p-8 bg-white shadow-lg border-t-4 border-[#CAB273] hover:-translate-y-1 transition-transform duration-300">
                <div className="bg-[#551756]/5 p-4 rounded-full mb-6">
                    <Truck className="w-8 h-8 text-[#551756]" />
                </div>
                <h3 className="font-serif text-xl text-[#551756] mb-3 font-bold">Fast Dispatch</h3>
                <p className="text-[#551756]/70 leading-relaxed">
                   We know you can't wait to wear your sparkle. We dispatch all orders within 24 hours with secure tracking.
                </p>
             </div>

             {/* Feature 3: Quality */}
             <div className="flex flex-col items-center text-center p-8 bg-white shadow-lg border-t-4 border-[#CAB273] hover:-translate-y-1 transition-transform duration-300">
                <div className="bg-[#551756]/5 p-4 rounded-full mb-6">
                    <ShieldCheck className="w-8 h-8 text-[#551756]" />
                </div>
                <h3 className="font-serif text-xl text-[#551756] mb-3 font-bold">Quality Checked</h3>
                <p className="text-[#551756]/70 leading-relaxed">
                  Each stone and clasp is triple-checked by our team before packing to ensure you receive flawless pieces.
                </p>
             </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default FashionJewelryStore;